// What you can do to the selected pad, slot or scene — the Pads screen's twin
// of the timeline's ClipActionBar.
//
// Docked under the grid, so it never covers the pad it acts on. On a phone it
// is a thumb-height row that scrolls sideways, every target 44px; on a wider
// screen the buttons carry their names. Presentational: it is handed what it
// may do and what to call when asked.

import { useEffect, useRef, useState, type ReactNode } from "react";
import {
  ArrowDownToLine,
  BetweenHorizontalStart,
  ClipboardPaste,
  Copy,
  CopyPlus,
  Pencil,
  Pin,
  Play,
  Plus,
  Scissors,
  Square,
  Trash2,
  X,
} from "lucide-react";
import { cn } from "@web-shared/lib/utils";
import { cleanClipName, cleanTrackName } from "@/lib/track-name";
import type { PadOps } from "@/account/hooks/usePadOps";

const MOD =
  typeof navigator !== "undefined" && /Mac|iPhone|iPad/.test(navigator.platform || navigator.userAgent)
    ? "⌘"
    : "Ctrl+";

export interface PadActionContext {
  /** Track name of the selection's lane, for the title. */
  laneName: string | null;
  /** The selected scene row's label, as the grid shows it. */
  sceneLabel: string | null;
  /** The selected row is one the grid offers from the timeline. */
  ghostRow: boolean;
  /** The selected pad is sounding (or queued): its play button stops. */
  playing: boolean;
  onPlay: () => void;
  onKeepRow: () => void;
}

export function PadActionBar({ ops, ctx }: { ops: PadOps; ctx: PadActionContext }) {
  const sel = ops.selection;
  const [renaming, setRenaming] = useState(false);
  const [draft, setDraft] = useState("");
  const input = useRef<HTMLInputElement | null>(null);
  // A rename belongs to the thing it started on.
  const selKey = sel ? JSON.stringify(sel) : "";
  useEffect(() => setRenaming(false), [selKey]);
  useEffect(() => {
    if (renaming) input.current?.select();
  }, [renaming]);
  if (!sel) return null;

  const title =
    sel.kind === "pad"
      ? cleanClipName(ops.clip?.name ?? "") || "Clip"
      : sel.kind === "slot"
        ? "Empty slot"
        : ctx.ghostRow
          ? `Row ${ctx.sceneLabel ?? ""}`
          : `Scene ${ctx.sceneLabel ?? sel.row + 1}`;
  const subtitle =
    sel.kind === "pad"
      ? `${ctx.laneName ? cleanTrackName(ctx.laneName) : ""}${ops.ghost ? " · from the timeline" : ""}`
      : sel.kind === "slot"
        ? `${ctx.laneName ? cleanTrackName(ctx.laneName) : ""} · scene ${sel.row + 1}`
        : ctx.ghostRow
          ? "Loops from the timeline"
          : "Scene";
  const startRename = () => {
    setDraft(sel.kind === "pad" ? (ops.clip?.name ?? "") : ctx.sceneLabel ?? "");
    setRenaming(true);
  };
  const commit = () => {
    ops.rename(draft);
    setRenaming(false);
  };

  return (
    <div
      className="flex shrink-0 items-center gap-3 border-t border-panelline bg-sunk px-3 py-2 pb-[max(0.5rem,env(safe-area-inset-bottom))] sm:px-5"
      role="toolbar"
      aria-label="Pad actions"
    >
      <div className="min-w-0 max-w-[40%] shrink-0 sm:max-w-[220px]">
        {renaming ? (
          <input
            ref={input}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              e.stopPropagation();
              if (e.key === "Enter") commit();
              else if (e.key === "Escape") setRenaming(false);
            }}
            onBlur={commit}
            maxLength={120}
            aria-label="Name"
            className="h-9 w-full rounded-md border border-panelline bg-panel px-2 text-sm font-semibold outline-none focus:border-white/40"
          />
        ) : (
          <>
            <div className="truncate text-sm font-semibold">{title}</div>
            <div className="truncate text-[11px] text-muted-foreground">{subtitle}</div>
          </>
        )}
      </div>

      <div className="flex min-w-0 flex-1 items-center gap-1 overflow-x-auto scrollbar-thin">
        {sel.kind === "pad" && (
          <>
            <Action label={ctx.playing ? "Stop" : "Play"} onClick={ctx.onPlay} icon={ctx.playing ? <Square className="size-4 fill-current" /> : <Play className="size-4 fill-current" />} />
            {ops.ghost && <Action label="Keep" onClick={ops.keep} icon={<Pin className="size-4" />} hint="as a scene clip" />}
            {!ops.ghost && <Action label="Duplicate" keys={`${MOD}D`} disabled={!ops.can.duplicate} onClick={ops.duplicate} icon={<CopyPlus className="size-4" />} />}
            <Divider />
            <Action label="Copy" keys={`${MOD}C`} disabled={!ops.can.copy} onClick={ops.copy} icon={<Copy className="size-4" />} />
            {!ops.ghost && <Action label="Cut" keys={`${MOD}X`} disabled={!ops.can.cut} onClick={ops.cut} icon={<Scissors className="size-4" />} />}
            <Action label="Paste" keys={`${MOD}V`} disabled={!ops.can.paste} onClick={ops.paste} icon={<ClipboardPaste className="size-4" />} hint="into the next empty slot below" />
            <Divider />
            <Action label="To timeline" onClick={ops.toTimeline} disabled={!ops.can.toTimeline} icon={<ArrowDownToLine className="size-4" />} hint="a copy at the insert marker" />
            {!ops.ghost && <Action label="Rename" onClick={startRename} disabled={!ops.can.rename} icon={<Pencil className="size-4" />} />}
            {!ops.ghost && <Action label="Delete" keys="Delete" danger disabled={!ops.can.remove} onClick={ops.remove} icon={<Trash2 className="size-4" />} />}
          </>
        )}
        {sel.kind === "slot" && (
          <>
            {ops.can.edit && (
              <Action label="New clip" keys="Enter" accent onClick={ops.edit} icon={<Plus className="size-4" />} hint="four bars, in the piano roll" />
            )}
            <Action label="Paste" keys={`${MOD}V`} disabled={!ops.can.paste} onClick={ops.paste} icon={<ClipboardPaste className="size-4" />} />
            {!ops.clipboard && <span className="px-2 text-xs text-muted-foreground">Copy a pad, then paste it here.</span>}
          </>
        )}
        {sel.kind === "scene" && (
          <>
            <Action label="Launch" onClick={ctx.onPlay} icon={<Play className="size-4 fill-current" />} />
            {ctx.ghostRow ? (
              <Action label="Keep as scene" accent onClick={ctx.onKeepRow} icon={<Pin className="size-4" />} hint="every loop in the row, as a new scene" />
            ) : (
              <>
                <Action label="Rename" onClick={startRename} disabled={!ops.can.rename} icon={<Pencil className="size-4" />} />
                <Action label="Insert" onClick={ops.insertScene} disabled={!ops.can.scene} icon={<BetweenHorizontalStart className="size-4" />} hint="an empty scene here" />
                <Action label="Duplicate" onClick={ops.duplicateScene} disabled={!ops.can.scene} icon={<CopyPlus className="size-4" />} />
                <Action label="Delete" keys="Delete" danger disabled={!ops.can.remove} onClick={ops.remove} icon={<Trash2 className="size-4" />} />
              </>
            )}
          </>
        )}
      </div>

      {ops.note && (
        <span role="status" aria-live="polite" className="hidden max-w-[28ch] truncate text-xs text-[var(--changed)] lg:inline" title={ops.note}>
          {ops.note}
        </span>
      )}
      <button
        type="button"
        onClick={() => ops.select(null)}
        aria-label="Done (Esc)"
        title="Done — Esc"
        className="flex size-11 shrink-0 items-center justify-center rounded-lg text-muted-foreground hover:bg-white/5 hover:text-foreground lg:size-9"
      >
        <X className="size-4" />
      </button>
    </div>
  );
}

function Divider() {
  return <span aria-hidden className="mx-0.5 h-6 w-px shrink-0 bg-panelline" />;
}

function Action({
  label,
  keys,
  hint,
  icon,
  disabled = false,
  danger,
  accent,
  onClick,
}: {
  label: string;
  keys?: string;
  hint?: string;
  icon: ReactNode;
  disabled?: boolean;
  danger?: boolean;
  accent?: boolean;
  onClick: () => void;
}) {
  const name = keys ? `${label} (${keys})` : label;
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      aria-label={name}
      title={[label, hint].filter(Boolean).join(" — ") + (keys ? ` · ${keys}` : "")}
      className={cn(
        // A thumb's 44px on a phone, an icon; a name beside it once there is room.
        "flex h-11 min-w-11 shrink-0 items-center justify-center gap-1.5 rounded-lg border border-panelline px-2.5",
        "text-xs font-semibold text-foreground/80 transition-colors hover:bg-white/5 hover:text-foreground",
        "disabled:pointer-events-none disabled:opacity-30 lg:h-9",
        accent && "border-transparent bg-[#f2c230] text-black hover:bg-[#f2c230]/90 hover:text-black",
        danger && "text-danger hover:bg-danger/10 hover:text-danger",
      )}
    >
      {icon}
      <span className={cn(!accent && "hidden md:inline")}>{label}</span>
    </button>
  );
}
